import React, { useState } from "react";
import { motion } from "framer-motion";

export default function ReflectionMirror({ onComplete }) {
  const [mirrorAngle, setMirrorAngle] = useState(0);
  const [sourceX, setSourceX] = useState(200);

  const width = 800;     // Wider workspace
  const height = 380;

  const mirrorX = width / 2;
  const hitY = height / 2;
  const hitX = mirrorX;

  const incidentX = sourceX;
  const incidentY = hitY;

  const rad = (mirrorAngle * Math.PI) / 180;
  const normalAngle = rad + Math.PI / 2;

  const dx = mirrorX - incidentX;
  const dy = hitY - incidentY;
  const rayAngle = Math.atan2(dy, dx);

  const incidence = rayAngle - normalAngle;
  const reflectedAngle = normalAngle - incidence;

  const reflectedX = hitX + Math.cos(reflectedAngle) * 200;
  const reflectedY = hitY + Math.sin(reflectedAngle) * 200;

  return (
    <div className="flex flex-col items-center mt-10 px-4">
      <h2 className="text-2xl md:text-3xl font-bold text-blue-700 mb-6">
        Reflection of Light
      </h2>

      {/* EXPERIMENT AREA (responsive container) */}
      <div
        className="relative border rounded bg-white shadow"
        style={{
          width: "100%",
          maxWidth: `${width}px`,
          height: `${height}px`,
        }}
      >
        {/* Mirror */}
        <motion.div
          className="absolute w-[6px] bg-gray-700 origin-center"
          style={{
            height: "100%",
            left: `${mirrorX - 3}px`,
            top: 0,
          }}
          animate={{ rotate: mirrorAngle }}
        />

        {/* Normal */}
        <motion.div
          className="absolute w-[2px] bg-green-500 origin-center"
          style={{
            height: "100%",
            left: `${mirrorX - 1}px`,
            top: 0,
            opacity: 0.5,
          }}
          animate={{ rotate: mirrorAngle + 90 }}
        />

        {/* RAYS */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {/* Incident Ray */}
          <line
            x1={incidentX}
            y1={incidentY}
            x2={hitX}
            y2={hitY}
            stroke="red"
            strokeWidth="4"
          />
          {/* Label */}
          <text
            x={(incidentX + hitX) / 2}
            y={(incidentY + hitY) / 2 - 10}
            fill="red"
            fontSize="14"
            fontWeight="bold"
          >
            Incident Ray
          </text>

          {/* Reflected Ray */}
          <line
            x1={hitX}
            y1={hitY}
            x2={reflectedX}
            y2={reflectedY}
            stroke="blue"
            strokeWidth="4"
          />
          {/* Label */}
          <text
            x={(hitX + reflectedX) / 2}
            y={(hitY + reflectedY) / 2 - 10}
            fill="blue"
            fontSize="14"
            fontWeight="bold"
          >
            Reflected Ray
          </text>

          {/* Point of Incidence */}
          <circle cx={hitX} cy={hitY} r="6" fill="orange" />
          <text
            x={hitX + 10}
            y={hitY + 20}
            fill="orange"
            fontSize="14"
            fontWeight="bold"
          >
            Point of Incidence
          </text>
        </svg>

        {/* Draggable Light Source */}
        <motion.div
          drag="x"
          dragConstraints={{
            left: 20,
            right: mirrorX - 80,
          }}
          onDrag={(e, info) => {
            setSourceX(info.point.x);
            onComplete && onComplete();
          }}
          className="absolute top-1/2 -translate-y-1/2 bg-yellow-300 w-12 h-12 rounded-full border-4 border-yellow-600 shadow cursor-pointer"
          animate={{ x: sourceX - 50 }}
        />
      </div>

      {/* CONTROLS */}
      <div className="mt-6 w-full max-w-[600px] p-4 bg-gray-50 rounded shadow">
        <label className="font-semibold block mb-2">
          Rotate Mirror: {mirrorAngle}°
        </label>
        <input
          type="range"
          min={-45}
          max={45}
          value={mirrorAngle}
          onChange={(e) => {
            setMirrorAngle(+e.target.value);
            onComplete && onComplete();
          }}
          className="w-full"
        />

        {/* Angle Readouts */}
        <div className="mt-4 text-center">
          <p className="text-blue-700 font-semibold text-lg">
            Angle of Incidence:{" "}
            {Math.abs(((incidence * 180) / Math.PI).toFixed(1))}°
          </p>

          <p className="text-green-700 font-semibold text-lg">
            Angle of Reflection:{" "}
            {Math.abs(((incidence * 180) / Math.PI).toFixed(1))}°
          </p>
        </div>
      </div>
    </div>
  );
}
